import { Button, Module } from '@wfp/react';
import {
  linkifyText,
  newLinesAsBr,
} from 'acr_ui/sources/components/dataPresentation/utils/dataProcessing';
import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  disabledBenDataNote2020,
  disabledBenDataNote2021,
  disabledBenDataNote2022,
} from 'src/components/ReportTemplate/Workspace/config';
import TinyEditorWrapper from 'src/components/ReportTemplate/WorkspaceACR2023/TinyEditorWrapper';
import {
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import { UpdateDataNotes } from 'src/redux/sections/actions';
import { RootState } from 'src/redux/store';

const DataNotes = () => {
  const dispatch = useDispatch();

  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.data,
  );
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const viewState = useSelector((state: RootState) => state.viewState);

  const [dataNotes, setDataNotes] = useState('');
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (sectionDetailData && sectionDetailData.data_notes) {
      setDataNotes(sectionDetailData.data_notes);
    } else {
      setDataNotes('');
    }
  }, [sectionDetailData, viewState.activeSectionID]);

  function onDataNotesSubmit() {
    const sanitizedDataNotes = DOMPurify.sanitize(dataNotes, {
      ADD_ATTR: ['target'],
    });
    dispatch(UpdateDataNotes(sanitizedDataNotes, viewState.activeSectionID));
    setIsEdit(false);
  }

  function handleOnChangeTextAreaDataNotes(value: string) {
    setDataNotes(value);
  }

  function renderBeneficiariesWithDisabilitiesDataNote() {
    if (!sectionDetailData.datasets) return false;
    const { total_actual_beneficiaries = {} } = sectionDetailData.datasets;
    const { beneficiaries_with_disabilities: { total = 0 } = {} } =
      total_actual_beneficiaries;
    if (total !== '0') {
      return !!total;
    }
    return false;
  }

  function handleCancelBtn() {
    setDataNotes(sectionDetailData.data_notes);
    setIsEdit(false);
  }

  if (!sectionDetailData) return null;
  if (!sectionDetailData.has_data_notes) return null;

  const isPublished = reportDetailData?.state === 'Published';

  let dataNotesContent;
  if (isEdit) {
    dataNotesContent = (
      <TinyEditorWrapper
        content={dataNotes}
        onChange={handleOnChangeTextAreaDataNotes}
        toolbar2=""
        allowLinks
      />
    );
  } else if (!dataNotes) {
    dataNotesContent = (
      <div className="data-notes-empty-info">No data notes</div>
    );
  } else {
    dataNotesContent = (
      <section
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(linkifyText(newLinesAsBr(dataNotes)), {
            ADD_ATTR: ['target'],
          }),
        }}
      />
    );
  }

  return (
    <Module
      className="data-notes-section special-module-title"
      data-test-id="data-notes-section"
    >
      <header className="view-mode-header">
        <h6 className="datasource-title" data-test-id="data-notes-title">
          Data Notes
        </h6>
        {!isEdit &&
          !isPublished &&
          sectionDetailData?.permissions?.can_edit_data_notes && (
            <span className="view-mode-group">
              <Button onClick={() => setIsEdit(true)}>Edit</Button>
            </span>
          )}
        {isEdit && (
          <span className="view-mode-group" id="data-notes-control">
            <Button onClick={handleCancelBtn} kind="secondary">
              Cancel
            </Button>
            <Button
              onClick={onDataNotesSubmit}
              data-test-id="save-data-notes-button"
            >
              Save data notes
            </Button>
          </span>
        )}
      </header>
      <div className="data-notes-extra">
        {sectionDetailData.data_notes_extra}
      </div>

      {renderBeneficiariesWithDisabilitiesDataNote() &&
        sectionDetailData.report.period === '2020' && (
          <div className="data-notes-extra"> {disabledBenDataNote2020}</div>
        )}

      {renderBeneficiariesWithDisabilitiesDataNote() &&
        sectionDetailData.report.period === '2021' && (
          <div className="data-notes-extra"> {disabledBenDataNote2021}</div>
        )}

      {renderBeneficiariesWithDisabilitiesDataNote() &&
        sectionDetailData.report.period === '2022' && (
          <div className="data-notes-extra">
            {disabledBenDataNote2022.replaceAll(
              '$$$',
              sectionDetailData.report.period,
            )}
          </div>
        )}
      <div className="view-mode-content data-notes">{dataNotesContent}</div>
    </Module>
  );
};

export default DataNotes;
